"use client";

import { cn } from "@/lib/utils/cn";
import {
  DEFAULT_THEME_ACCENT,
  THEME_ACCENT_PRESETS,
  deriveSidebarPalette,
  normalizeAccentColor,
  readableForeground,
} from "@/lib/theme/accent-color";
import type { ThemeMode } from "@/store/theme-store";

type ProfileThemeSettingsProps = {
  themeMode: ThemeMode;
  accentColor: string;
  onThemeModeChange: (mode: ThemeMode) => void;
  onAccentColorChange: (color: string) => void;
};

export function ProfileThemeSettings({
  themeMode,
  accentColor,
  onThemeModeChange,
  onAccentColorChange,
}: ProfileThemeSettingsProps) {
  const normalizedAccent = normalizeAccentColor(accentColor) ?? DEFAULT_THEME_ACCENT;
  const sidebarPreview = deriveSidebarPalette(normalizedAccent);

  return (
    <section className="admin-surface grid gap-ds-5 rounded-card p-ds-5">
      <div className="grid gap-ds-1">
        <h2 className="text-title font-semibold text-foreground">Appearance</h2>
        <p className="text-caption text-muted">
          Choose light or dark mode and pick an accent color. In light mode, accent colors apply to buttons, links, and the navigation menu.
        </p>
      </div>

      <div className="grid gap-ds-3">
        <span className="form-label">Theme mode</span>
        <div className="segment-tabs">
          {(["light", "dark"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onThemeModeChange(mode)}
              className={cn("segment-tab capitalize", themeMode === mode && "segment-tab-active")}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-ds-3">
        <span className="form-label">Accent color (light mode)</span>
        <div className="flex flex-wrap gap-ds-2">
          {THEME_ACCENT_PRESETS.map((preset) => {
            const active = normalizedAccent === preset.color;
            return (
              <button
                key={preset.id}
                type="button"
                title={preset.label}
                aria-label={`${preset.label} accent`}
                onClick={() => onAccentColorChange(preset.color)}
                className={cn(
                  "h-10 w-10 rounded-full border-2 transition-transform hover:scale-105",
                  active ? "border-foreground ring-2 ring-offset-2 ring-offset-card" : "border-card-border",
                )}
                style={{ backgroundColor: preset.color }}
              />
            );
          })}
        </div>

        <label className="grid max-w-xs gap-ds-2">
          <span className="form-helper">Custom color</span>
          <div className="flex items-center gap-ds-3">
            <input
              type="color"
              value={normalizedAccent}
              onChange={(event) => onAccentColorChange(event.target.value)}
              className="h-10 w-14 cursor-pointer rounded-control border border-card-border bg-surface-primary p-1"
              aria-label="Custom accent color"
            />
            <input
              type="text"
              value={normalizedAccent}
              onChange={(event) => onAccentColorChange(event.target.value)}
              className="ui-control h-10 flex-1 font-mono uppercase"
              spellCheck={false}
              maxLength={7}
              placeholder="#2563eb"
            />
          </div>
        </label>

        <div
          className="rounded-card border border-card-border bg-surface-secondary p-ds-4"
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, ${normalizedAccent} 14%, white), white)`,
          }}
        >
          <p className="text-caption font-semibold" style={{ color: normalizedAccent }}>
            Preview accent
          </p>
          <p className="mt-ds-1 text-micro text-muted">
            Buttons, links, and the sidebar menu background follow this color in light mode.
          </p>
          <div className="mt-ds-3 flex flex-wrap items-stretch gap-ds-3">
            <button
              type="button"
              className="btn btn-sm"
              style={{ backgroundColor: normalizedAccent, color: readableForeground(normalizedAccent) }}
            >
              Sample button
            </button>
            <div
              className="flex min-w-[8rem] flex-col justify-center rounded-control border px-ds-3 py-ds-2 text-micro"
              style={{
                backgroundColor: sidebarPreview.bg,
                borderColor: sidebarPreview.border,
                color: sidebarPreview.fg,
              }}
            >
              <span className="font-semibold opacity-90">Menu preview</span>
              <span style={{ color: sidebarPreview.muted }}>Navigation text</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
